// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx, ParamComponent } from 'panel';
import { VNode } from 'snabbdom';
import { selx } from 'clsx';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { CurrentProject } from 'common/report/model-resources/projects';
import { IronContextRegistry } from 'common/report/context-registry';
import Layout from 'common/stylesheets/mixins/layout.json';
import { KEYDOWN_EVENTS, PANEL_MIN_WIDTH, SIDEBAR_DEFAULT_RATIO } from 'common/components/resizable-panel/constants';
import { ResizablePanelEl } from 'common/components/resizable-panel';
import { PerfMeasure } from 'common/report/perf-measure';
import { currentProjectStore } from 'common/report/stores/project-details';
import { Direction, PanelName } from 'common/components/resizable-panel/types';
import { useStoreAndFetchOnce } from 'common/util/component-helpers';
import { HORIZONTAL_CONTENT_PADDING_STYLE } from 'iron/common/report/layout/banner-layout';
import { ContentFrame } from 'common/report/layout/banner-layout/content-frame';
import { isTestEnv } from 'common/report/util/env';
import { ReportMessageBannerEl } from 'common/widgets/report-message-banner';
import { ReportBannersEl } from 'common/widgets/report-banners';
import { MetricsBetaButtonEl } from 'common/query-builder/metrics-beta-button/panel';
import styles from './index.module.styl';
const COMPONENT_NAME = "mp-fullwidth-report";
const {
  PAGE_MIN_WIDTH
} = Layout;
type Params = {
  hasMultipleMetrics?: boolean;
  isSegmentationLimitHit?: boolean;
  exportSegmentationLimit?: number;
  segmentationLimit?: number;
  reportSamplingFactor?: number;
  reportHeader: VNode;
  queryBuilder: VNode;
  panelHeader?: VNode;
  report: VNode;
};
type FullWidthReportJsxProps = {
  params: Params;
  on: {
    exportcsvclicked?: (ev: CustomEvent) => void;
  };
};
export interface FullWidthReportState {
  QBAlignment: Direction;
}

/**
 * Layout component for a full width report
 * Provides a sidebar and report content sections both independently scrollable
 *
 * @TODO [pablo] make this component query builder aware by rendering report specific
 * components (report header or qb) by itself taking a config object
 */
export class FullWidthReport extends ParamComponent<Params, FullWidthReportState, IronContextRegistry> {
  getCurrentProject(): CurrentProject {
    return this.getContext("currentProject").get().value;
  }
  isUserSidebarOpen() {
    return this.getContext("reportLayoutManager").getIsUserSidebarOpen();
  }
  get config() {
    return {
      contexts: ["reportLayoutManager" as const, "currentProject" as const],
      defaultState: {
        QBAlignment: Direction.Right
      },
      params: {
        exportSegmentationLimit: Number,
        hasMultipleMetrics: Boolean,
        isSegmentationLimitHit: Boolean,
        segmentationLimit: Number,
        reportSamplingFactor: Number,
        reportHeader: Object,
        queryBuilder: Object,
        panelHeader: Object,
        report: Object
      },
      template: () => this.renderTemplate()
    };
  }
  renderTemplate() {
    const minContentWidth = PAGE_MIN_WIDTH - PANEL_MIN_WIDTH;
    return <div sel={selx(styles.root)} class={{
      'inherit-size': true
    }}>
        <div sel={selx(styles.fullwidthReportContainer)} class={{
        [styles.hideQb]: this.state.QBAlignment === Direction.Right && this.isUserSidebarOpen(),
        [styles.rightAlignQb]: this.state.QBAlignment === Direction.Right,
        [styles.leftAlignQb]: this.state.QBAlignment === Direction.Left
      }}>
          <div sel={selx(styles.reportHeader)}>{this.params.reportHeader}</div>
          <div attrs={{
          elref: "fullWidthReport"
        }} sel={selx(styles.fullwidthReport)}>
            <ResizablePanelEl sel={selx(styles.sidebar)} attrs={{
            elref: "fullWidthReportPanel",
            'storage-key': "query-builder",
            'min-adjacent-size': minContentWidth,
            'show-collapse-button': true,
            'show-alignment-toggle': true,
            'panel-name': PanelName.QueryPanel,
            // When the sidenav is enabled in reports, we right-align the QB by default to make room.
            'default-alignment': Direction.Right,
            'disable-responsive-resize': true,
            'collapse-key': KEYDOWN_EVENTS.Digit1,
            'default-ratio': SIDEBAR_DEFAULT_RATIO
          }} on={{
            panelResize: ev => {
              this.getContext("reportLayoutManager").updateIsQbCollapsed(ev.detail.isCollapsed);
              if (!ev.detail.isCollapsed) {
                const width = ev.detail.size;
                this.getContext("reportLayoutManager").updateRightPanelWidth(width);
              }
            },
            updateAlignment: ev => {
              this.update({
                QBAlignment: ev.detail.newAlignment
              });
              this.getContext("reportLayoutManager").updateQbAlignment(ev.detail.newAlignment);
            }
          }} props={{
            headerContent: this.params.panelHeader,
            footerContent: <MetricsBetaButtonEl />
          }}>
              <div sel={selx(styles.queryBuilder)}>{this.params.queryBuilder}</div>
            </ResizablePanelEl>
            <ContentFrame sel={selx(styles.rightPanel)}
          // TODO(mack): core reports shouldn't use ContentFrame because they behave differently enough from
          // basic reports that they should have their own layout logic.
          isCoreReport>
              <div style={{
              padding: "0 ".concat(HORIZONTAL_CONTENT_PADDING_STYLE, " 0 ").concat(HORIZONTAL_CONTENT_PADDING_STYLE)
            }}>
                {!isTestEnv() && <>
                    <ReportMessageBannerEl />
                    <ReportBannersEl attrs={{
                  'export-segmentation-limit': this.params.exportSegmentationLimit,
                  'has-multiple-metrics': this.params.hasMultipleMetrics,
                  'is-segmentation-limit-hit': this.params.isSegmentationLimitHit,
                  'report-sampling-factor': this.params.reportSamplingFactor,
                  'segmentation-limit': this.params.segmentationLimit
                }} />
                  </>}
              </div>
              <div sel={selx(styles.reportContainer)}>{this.params.report}</div>
            </ContentFrame>
          </div>
        </div>
      </div>;
  }
  connectedCallback() {
    const renderMeasure = new PerfMeasure("render-report-container").start();
    super.connectedCallback();
    const whiteBackground = this.getContext("reportLayoutManager").getWhiteBackBackground();
    this.getContext("reportLayoutManager").updateWhiteBackBackground(true);
    this.onDisconnected(() => this.getContext("reportLayoutManager").updateWhiteBackBackground(whiteBackground));
    useStoreAndFetchOnce(this, currentProjectStore);
    requestAnimationFrame(() => renderMeasure.end());
  }
}
export const FullWidthReportEl = defineAndMakeJsxFactory<FullWidthReportJsxProps>(COMPONENT_NAME, FullWidthReport);
export const FullWidthReportReact = panel2react<FullWidthReportJsxProps>(COMPONENT_NAME, FullWidthReport);